import React, { useCallback, useRef, useState } from 'react'

import classNames from 'clsx'
import PropTypes from 'prop-types'
import { omit } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { makeStyles } from '@mui/styles'

import { Mention, MentionsInput } from 'react-mentions'

import config from '~/src/App/config'
import createLogger from '~/src/Lib/Logging'
import { EMPTY_OBJECT } from '~/src/Lib/Utils'
import Formatted from '~/src/UI/Shared/SmartText'

export * from './utils'

const displayName = 'DescriptionMentions'
const logger = createLogger(displayName)

const appName = config.APP.toLowerCase()

const useStyles = makeStyles(theme => ({
  asText: {
    [`& .${appName}__highlighter__substring`]: {
      color: theme.palette.gray,
    },
  },
  wrap: {
    flex: 1,
    [`& .${appName}__control`]: {
      position: 'relative',
      minHeight: '4rem',
      marginBottom: '8px',
      ...theme.typography.body1,
    },
    [`& .${appName}__input, & .${appName}__highlighter`]: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      padding: `${theme.spacing(1.5, 2)}`,
      fontSize: 'inherit',
      lineHeight: 'inherit',
      letterSpacing: 'inherit',
    },
    [`& .${appName}__input`]: {
      caretColor: theme.palette.text.primary,
      backgroundColor: `${theme.palette.enabled} !important`,
      color: 'transparent',
      borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
      border: 'none',
      borderBottom: `solid 1px ${theme.palette.primary.main}`,
      outline: 'none',
      '&:hover': {
        backgroundColor: `${theme.palette.primary.hovered} !important`,
      },
      '&:focus': {
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.focused} !important`,
      },
      '&::placeholder': {
        color: theme.palette.primary.main,
        transition: theme.transitions.create('color')
      },
      '&:focus::placeholder': {
        color: theme.palette.text.secondary,
      },
    },
    [`& .${appName}__highlighter > *`]: {
      fontSize: 'inherit !important',
      lineHeight: 'inherit !important',
      letterSpacing: 'inherit !important',
    },
    [`& .${appName}__highlighter__substring`]: {
      color: theme.palette.text.primary,
      visibility: 'visible !important',
    },
    [`& .${appName}__mention`]: {
      color: theme.palette.primary.main,
    },
  },
  '@global': {
    [`.${appName} .${appName}__suggestions`]: {
      minWidth: 'inherit !important',
      backgroundColor: 'transparent !important',
      transform: 'translate3d(0, 1rem, 0)',
    },
    [`.${appName} .${appName}__suggestions__list`]: {
      overflowX: 'auto',
      height: '100%',
      maxHeight: '30vh',
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[5],
    },
    [`.${appName} .${appName}__suggestions__item`]: {
      padding: '8px 16px !important',
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      fontSize: '0.875rem',
      fontWeight: '500',
      '&:hover': {
        backgroundColor: theme.palette.hovered,
      }
    },
  },
  mentioned: {
    color: theme.palette.primary.main,
    fontSize: '14px',
    fontWeight: 'bold !important',
  }
}), { name: displayName })

const fieldOmitter = omit(['meta'])

/**
 * Allows users to mention other members of the facility in a textarea field
 * @component
 * @alias DescriptionMentions
 * @named_export Component
 * @example
 * import { faker } from '@faker-js/faker'
 * import { useSetState } from '~/src/Lib/Utils/hooks'
 * {() => {
 *   const [mentionable] = React.useState(() => Array.from({ length: 10 }, () => ({
 *     id: faker.datatype.number(),
 *     display: faker.name.findName(),
 *   })))
 *   const [mentionState, setMentionState] = useSetState(() => ({
 *     targetValue: '',
 *     mentionValue: '',
 *     textValue: '',
 *     mentions: [],
 *   }))
 *   const onChange = React.useCallback((event, mentionValue, textValue, mentions) => setMentionState({
 *     targetValue: event.target.value,
 *     mentionValue,
 *     textValue,
 *     mentions,
 *   }), [setMentionState])
 *   console.log('[mentionState]', mentionState)
 *
 *   return (
 *     <Box display="flex" gap="1ch">
 *       <DescriptionMentions
 *         data={mentionable}
 *         value={mentionState.textValue}
 *         onChange={onChange}
 *       />
 *       <Box flex="0 0 50%">
 *         <T><strong>input value</strong>: {mentionState.targetValue}</T>
 *         <T><strong>mention value</strong>: {mentionState.mentionValue}</T>
 *         <T><strong>text value</strong>: {mentionState.textValue}</T>
 *         <T><strong>mentions</strong>:<pre><code>{JSON.stringify(mentionState.mentions, null, 2)}</code></pre></T>
 *       </Box>
 *     </Box>
 *   )
 * }}
 */
export const Component = ({
  asText = false,
  asTextProps = { variant: 'body2' },
  field = EMPTY_OBJECT,
  form = EMPTY_OBJECT,
  data = EMPTY_OBJECT,
  label: propsLabel = null,
  value: propsValue = '',
  customChange = null,
  name: propsName = 'description',
  onAdd = undefined,
  timeout = 1000,
  ...restProps
}) => {
  const parentValue = field.value ?? propsValue

  const [value, setValue] = useState(parentValue)
  const timeoutId = useRef(null)

  const fieldName = field.name ?? propsName

  const onChangeFormik = useCallback((mentionsValue, mentions) => {
    form.setFieldValue(fieldName, mentionsValue)
    form.setFieldValue(`${fieldName}Mentions`, mentions.map(mention => parseInt(mention.id, 10)))
  }, [fieldName, form])

  const handleChange = useCallback((ignore1, mentionsValue, ignore2, mentions) => {
    setValue(mentionsValue)
    if (timeoutId.current) {
      clearTimeout(timeoutId.current)
    }
    timeoutId.current = setTimeout(() => {
      if (customChange) {
        customChange({
          [`${fieldName}Mentions`]: mentions.map(mention => parseInt(mention.id, 10)),
          [fieldName]: mentionsValue,
        })
        return
      }
      onChangeFormik(mentionsValue, mentions)
    }, timeout)
  }, [customChange, fieldName, onChangeFormik, timeout])

  const classes = useStyles()

  if (asText) {
    if (!parentValue) return null
    return <Formatted text={parentValue} TypographyProps={asTextProps} classes={classes} />
  }

  const label = propsLabel ?? field.label ?? 'Description'

  return (
    <div className={classNames(classes.wrap, asText && classes.asText)}>
      <MentionsInput
        {...fieldOmitter(field)}
        onChange={handleChange}
        {...restProps}
        aria-label={label}
        className={appName}
        suggestionsPortalHost={document.getElementById('mentionsRoot')}
        value={value}
      >
        <Mention
          appendSpaceOnAdd
          className={`${appName}__mention`}
          data={data}
          displayTransform={(_, name) => `@${name}`}
          onAdd={onAdd}
          trigger="@"
        />
      </MentionsInput>
    </div>
  )
}
Component.displayName = displayName
Component.propTypes = {
  app: PropTypes.string,
  asText: PropTypes.bool,
  asTextProps: PropTypes.shape({
    color: PropTypes.oneOf(['primary', 'secondary', 'textPrimary', 'textSecondary']),
    variant: PropTypes.string,
    noWrap: PropTypes.bool,
    gutterBottom: PropTypes.bool,
  }),
  customChange: PropTypes.func,
  data: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    display: PropTypes.node
  })).isRequired,
  field: PropTypes.shape({
    name: PropTypes.string,
    placeholder: PropTypes.string,
  }),
  form: PropTypes.shape({
    setFieldValue: PropTypes.func,
  }),
  label: PropTypes.string,
  name: PropTypes.string,
  onAdd: PropTypes.func,
  timeout: PropTypes.number,
  value: PropTypes.string,
}

const Memoized = React.memo(props => {
  const { mentionable } = useConnect('selectMentionable')

  return <Component {...props} data={mentionable} />
})

export default Memoized
